import { defineComponent as _defineComponent } from 'vue'
import { PropType, Ref, ref, onMounted, toRefs, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGameStore } from '@/store/useGameStore'
import { useProjectStore } from '@/store/useProjectStore'
import { useAppStore } from '@/store/useAppStore'
import { shuffleItems, playSounds, stopSounds } from '@/utilities'
import { Enig, TASK_TYPES } from '@/templates/slplus/structure/questionModels'
import { Effect, load, transform, play } from '@/webAudio/index'

import Button from '@/components/base/Button.vue'
import Avatar from '@/components/base/Avatar.vue'
import RecordButton from '@/components/base/RecordButton.vue'
import Intro from '@/components/task/Intro.vue'

import yourTurnImage from '@/assets/tasks/retell/yourturn.svg'
import parrotBackground from '@/assets/tasks/enig/parrot_bg.svg'
import robotBackground from '@/assets/tasks/enig/robot_bg.svg'
import parrotIcon from '@/assets/tasks/enig/parrotIcon.svg'
import robotIcon from '@/assets/tasks/enig/robotIcon.svg'

import setning1abc from '@/assets/tasks/enig/EMO_si_setning1abc.mp3'
import yourTurnSound from '@/assets/tasks/enig/Enig_med_Omar_tur.mp3'

import { DialogConfig, Player, Sett, TrackingDetails, TrackingSearch, TRACKING_TYPE } from '@/types/main'
import { useDeviceService, MediaType } from '@/composition/useDevice'

interface FinishedPlayer {
  mode: MODES
  player: Player
}

enum MODES {
  intro = 'intro',
  playerRecord = 'playerRecord',
  endRecording = 'endRecording',
  allDone = 'allDone',

  // Recording
  stop = 'stop',
  record = 'record',
  play = 'play',
  delete = 'delete',

  // Playback
  normal = 'normal',
  parrot = 'parrot',
  robot = 'robot',
}


export default /*#__PURE__*/_defineComponent({
  __name: 'VoiceRecording',
  props: {
  question: { required: true, type: Object as PropType<Enig> },
  sett: { type: Object as PropType<Sett>, required: true },
  round: { required: false, type: Number, default: 0 },
},
  emits: ['review', 'done', 'playsound'],
  setup(__props, { expose }) {
  expose();

const props = __props

const messages = {
  no: {
    recordingIntroTitle: 'Hva forstod du?',
    recordingIntro: 'Si en setning om filmen, ta den opp og hør på den',
    itsYourTurn1: 'det er din tur!',
    itsYourTurn2: 'Si noe om filmen',
    dialogDeleteTitle: 'Slett denne lydopptak',
    dialogDeleteText: 'Hvis du sletter, du kan ikke angre',
    dialogDeleteConfirm: 'Slette',
    dialogDeleteCancel: 'Avbryt',
    goodJob: 'Godt jobbet!',
  },
  en: {
    recordingIntroTitle: 'What did you understand?',
    recordingIntro: 'Say a sentence about the film, record and listen to it',
    itsYourTurn1: 'it is your turn!',
    itsYourTurn2: 'Say something about the film',
    dialogDeleteTitle: 'Delete this audio recording',
    dialogDeleteText: 'Deletion cannot be undone',
    dialogDeleteConfirm: 'Delete',
    dialogDeleteCancel: 'Cancel',
    goodJob: 'Well done!',
  },
}
const { t } = useI18n({ messages })




const { getters: deviceGetters, actions: deviceActions } = useDeviceService()
const { getters: gameGetters, actions: gameActions } = useGameStore()
const { getters: projectGetters } = useProjectStore()
const { actions: appActions } = useAppStore()

const { sett, question } = toRefs(props)
const players = gameGetters.playersInSelectedGame
const currentPlayer: Ref<Player | undefined> = ref()
const mode: Ref<MODES> = ref(MODES.intro)
const isRecording = ref(false)
const isPlaying = ref(false)
const recordingExists = ref(false)
let shuffledPlayers = shuffleItems(players.value)
let currentTracking: TrackingDetails | undefined
let currentPlayerAudio: HTMLAudioElement
let playbackMode = ref(MODES.normal)
const isMobile = deviceGetters.deviceReady

const finishedPlayers: FinishedPlayer[] = []

onMounted(() => {
  changeMode(MODES.intro)
})

const playerBackground = (pbMode: MODES, player?: Player) => {
  let bg = { backgroundColor: 'white', backgroundImage: '' }
  switch (pbMode) {
    case MODES.normal:
      bg = { backgroundImage: '', backgroundColor: player?.profile.avatar.backgroundColour || 'white' }
      break
    case MODES.parrot:
      bg = { backgroundColor: '', backgroundImage: `url(${parrotBackground})` }
      break
    case MODES.robot:
      bg = { backgroundColor: '', backgroundImage: `url(${robotBackground})` }
      break
  }
  return bg
}

const computedPlayerBackground = computed(() => playerBackground(playbackMode.value, currentPlayer.value))

// Make a tracking to represent this page's recording
const createTracking = () => {
  const game = gameGetters.selectedGame.value
  const project = projectGetters.selectedProject.value
  if (game && project && question.value) {
    currentTracking = new TrackingDetails({
      itemID: `${sett.value._id}-${question.value._id}`,
      gameID: game._id,
      playerIDs: currentPlayer.value ? [currentPlayer.value._id] : ['player ID unknown'],
      projectID: project._id,
      type: TRACKING_TYPE.question,
      details: {
        taskType: TASK_TYPES.enig,
        taskInfo: question.value.name,
        playerStorage1: playbackMode.value,
        // page: currentPageIndex.value,
      },
    })
  }
}

// Recording
const startRecording = () => {
  isRecording.value = true
  deviceActions.createAudio().then(async () => {
    await deviceActions.startRecordingAudio()
  })
}

const stopRecording = async () => {
  await deviceActions.stopRecordingAudio()
  isRecording.value = false
  if (deviceGetters.audioFilename.value) {
    await loadAudioBuffer(deviceGetters.audioFilename.value)
    recordingExists.value = true
    if (currentTracking) {
      currentTracking.audioFile = deviceGetters.audioFilename.value
      currentTracking.isMedia = true
      currentTracking.details.playerStorage1 = playbackMode.value as string
    }
  }
}

const stopPlayingCallback = () => {
  isPlaying.value = false
}

const startPlaying = async () => {
  if (isPlaying.value) return
  const media = deviceGetters.currentAudioBuffer.value
  if (media) {
    await load(media)
    switch (playbackMode.value) {
      case MODES.normal:
        play(stopPlayingCallback)
        break
      case MODES.parrot:
        await transform(Effect.speed, 1.3)
        await transform(Effect.pitch, 0.5)
        play(stopPlayingCallback)
        break
      case MODES.robot:
        await transform(Effect.giantRobot)
        play(stopPlayingCallback)
        break
      default:
        break
    }
    isPlaying.value = true
  }
}

const stopPlaying = () => {
  stopSounds()
  if (currentPlayerAudio) {
    currentPlayerAudio.pause()
    currentPlayerAudio.currentTime = 0
    isPlaying.value = false
  }
}

const deleteRecordedAudio = () => {
  if (deviceGetters.audioFilename.value) recordingExists.value = false
  const media = deviceGetters.audioFilename.value
  const dialogConfig: DialogConfig = {
    title: t('dialogDeleteTitle'),
    visible: true,
    text: t('dialogDeleteText'),
    cancel: () => appActions.setDialog(false),
    cancelText: t('dialogDeleteCancel'),
    confirm: () => {
      appActions.setDialog(false)
      if (media) {
        deviceActions.removeMedia(media, MediaType.audio).then(() => {
          recordingExists.value = false
        })
      }
    },
    confirmText: t('dialogDeleteConfirm'),
  }
  appActions.setDialog(true, dialogConfig)
}

function recordButtonClicked(mode: MODES) {
  switch (mode) {
    case MODES.stop:
      if (isPlaying.value) stopPlaying()
      else stopRecording()
      break
    case MODES.play:
      startPlaying()
      break
    case MODES.record:
      startRecording()
      break
    default:
      break
  }
}

async function loadAudioBuffer(filename: string) {
  return deviceActions.loadAudioBuffer(filename).then(() => {
    recordingExists.value = true
  })
}

async function getUserRecording() {
  const search: TrackingSearch = {
    gameID: gameGetters.selectedGame.value?._id || '',
    itemID: `${sett.value._id}-${question.value._id}`,
    playerIDs: [currentPlayer.value?._id || ''],
    isMedia: true, // Only find Trackings with attached audio
  }
  const trackings = gameActions.findMatchingTrackings(search) as TrackingDetails[]
  // gameActions.findMatchingTrackings() returns an array reverse-sorted by date, so the first matching value will be the newest
  // Array.find() returns the first matching value
  const t = trackings.find((t) => t.details && t.details.taskType === TASK_TYPES.enig)
  currentTracking = new TrackingDetails(t)
  if (t && currentTracking && currentTracking.audioFile) {
    await loadAudioBuffer(currentTracking.audioFile)
    playbackMode.value = (currentTracking.details.playerStorage1 as MODES) || MODES.normal
  } else if (!t) {
    createTracking()
  }
}

const changeMode = (newMode: MODES) => {
  let changedMode = newMode
  stopSounds()
  switch (newMode) {
    case MODES.intro:
      shuffledPlayers = shuffleItems(players.value)
      playSounds([setning1abc])
      break
    case MODES.playerRecord:
      currentPlayer.value = shuffledPlayers.pop()
      getUserRecording()
      playSounds([yourTurnSound])
      break
    case MODES.endRecording:
      if (currentPlayer.value) finishedPlayers.push({ mode: playbackMode.value, player: currentPlayer.value })
      if (shuffledPlayers.length > 0) changedMode = MODES.playerRecord
      else changedMode = MODES.allDone
      playbackMode.value = MODES.normal
      if (currentTracking) {
        currentTracking.complete()
        gameActions.commitNewTracking(currentTracking)
      }
      recordingExists.value = false
      break
    case MODES.allDone:
      break
    default:
      break
  }
  changedMode !== newMode ? changeMode(changedMode) : (mode.value = newMode)
}

const __returned__ = { messages, t, MODES, props, deviceGetters, deviceActions, gameGetters, gameActions, projectGetters, appActions, sett, question, players, currentPlayer, mode, isRecording, isPlaying, recordingExists, shuffledPlayers, currentTracking, currentPlayerAudio, playbackMode, isMobile, finishedPlayers, playerBackground, computedPlayerBackground, createTracking, startRecording, stopRecording, stopPlayingCallback, startPlaying, stopPlaying, deleteRecordedAudio, recordButtonClicked, loadAudioBuffer, getUserRecording, changeMode, Button, Avatar, RecordButton, Intro, yourTurnImage, parrotIcon, robotIcon }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})